import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import * as connectionService from '../services/connectionService';
import userService from '../services/userService';

// Async thunks
export const fetchUserConnections = createAsyncThunk(
  'connections/fetchUserConnections',
  async ({ userId, type = 'all' }, { rejectWithValue }) => {
    try {
      const response = await connectionService.getUserConnections(userId, type);
      return response;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchPendingConnections = createAsyncThunk(
  'connections/fetchPendingConnections',
  async ({ userId, direction }, { rejectWithValue }) => {
    try {
      console.log(`Fetching ${direction} pending connection requests for user ${userId}`);
      const response = await connectionService.getPendingConnections(userId, direction);
      console.log(`Raw API response for ${direction} pending connections:`, response);
      
      // Determine the requests array based on different potential response formats
      let requests = [];
      
      // Check if response is an array
      if (Array.isArray(response)) {
        requests = response;
        console.log(`Response is an array with ${requests.length} requests`);
      } 
      // Check if response has a 'requests' property (common API pattern)
      else if (response && response.requests && Array.isArray(response.requests)) {
        requests = response.requests;
        console.log(`Found ${requests.length} requests in response.requests`);
      }
      // Check if response is an object with the requests
      else if (response && typeof response === 'object') {
        // This might be the case if the API returns the raw requests object
        requests = [response];
        console.log('Response is a single request object, converting to array');
      }
      
      console.log(`Final processed requests before user details: ${requests.length}`, requests);
      
      // If no requests were found, return empty array
      if (requests.length === 0) {
        return {
          direction,
          requests: []
        };
      }
      
      // Get unique user IDs from both requesters and recipients
      const userIds = new Set();
      requests.forEach(request => {
        if (request.requesterId) userIds.add(request.requesterId);
        if (request.recipientId) userIds.add(request.recipientId);
      });
      
      console.log(`Found ${userIds.size} unique user IDs in requests:`, [...userIds]);
      
      // Fetch user details for all users involved
      const users = await userService.fetchUsersBatch([...userIds]);
      console.log(`Fetched ${users.length} users:`, users);
      
      const usersMap = new Map(users.map(user => [user.id, user]));
      
      // Enhance requests with user details
      const enhancedRequests = requests.map(request => {
        // Normalize request ID field - ensure it's consistent
        const normalizedId = request._id || request.id || request.requestId;
        
        // Create enhanced request with all possible fields
        const enhancedRequest = {
          ...request,
          id: normalizedId,
          _id: normalizedId,
          requestId: normalizedId,
          // Add user objects based on IDs
          requester: usersMap.get(request.requesterId) || { id: request.requesterId, name: `User (${request.requesterId?.substring(0, 8)})` },
          recipient: usersMap.get(request.recipientId) || { id: request.recipientId, name: `User (${request.recipientId?.substring(0, 8)})` }
        };
        
        console.log(`Enhanced request ${normalizedId}:`, {
          requesterName: enhancedRequest.requester?.name,
          recipientName: enhancedRequest.recipient?.name,
          type: enhancedRequest.requestType || enhancedRequest.type
        });
        
        return enhancedRequest;
      });
      
      console.log(`Final enhanced ${direction} requests: ${enhancedRequests.length}`);
      
      return {
        direction,
        requests: enhancedRequests
      };
    } catch (error) {
      console.error('Error fetching pending connections:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const sendConnectionRequest = createAsyncThunk(
  'connections/sendConnectionRequest',
  async ({ requesterId, recipientId, requestType, message }, { rejectWithValue }) => {
    try {
      const response = await connectionService.sendConnectionRequest(
        requesterId,
        recipientId,
        requestType,
        message
      );
      return response;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const sendBatchConnectionRequests = createAsyncThunk(
  'connections/sendBatchConnectionRequests',
  async ({ requesterId, recipientIds, requestType, message }, { rejectWithValue, dispatch }) => {
    try {
      console.log(`Sending batch connection requests to ${recipientIds.length} recipients`);
      
      const responses = await connectionService.sendBatchConnectionRequests(
        requesterId,
        recipientIds,
        requestType,
        message
      );
      
      return {
        responses,
        recipientIds,
        requestType
      };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const acceptConnection = createAsyncThunk(
  'connections/acceptConnection',
  async ({ requestId, userId }, { rejectWithValue }) => {
    try {
      const response = await connectionService.acceptConnectionRequest(requestId, userId);
      return { ...response, requestId, action: 'accept' };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const rejectConnection = createAsyncThunk(
  'connections/rejectConnection',
  async ({ requestId, userId }, { rejectWithValue }) => {
    try {
      const response = await connectionService.rejectConnectionRequest(requestId, userId);
      return { ...response, requestId, action: 'reject' };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const removeConnection = createAsyncThunk(
  'connections/removeConnection',
  async ({ connectionId, userId }, { rejectWithValue }) => {
    try {
      // Log the connection object for debugging
      console.log("Removing connection with data:", { connectionId, userId });
      
      // Validate input parameters
      if (!connectionId) {
        console.error("Missing connectionId parameter");
        return rejectWithValue({ message: "Connection ID is required" });
      }
      
      if (!userId) {
        console.error("Missing userId parameter");
        return rejectWithValue({ message: "User ID is required" });
      }
      
      // Attempt to remove the connection
      const response = await connectionService.removeConnection(connectionId, userId);
      
      // Extract the ID to use for filtering in the reducer
      let idForFiltering;
      
      if (typeof connectionId === 'object' && connectionId !== null) {
        // If connectionId is an object, extract the ID
        idForFiltering = connectionId.id || connectionId._id || 
                        connectionId.connectedUserId || connectionId.userId;
      } else {
        // Otherwise use the connectionId as is
        idForFiltering = connectionId;
      }
      
      return { 
        ...response, 
        connectionId: idForFiltering,
        originalConnection: connectionId // Keep the original connection object for reference
      };
    } catch (error) {
      console.error("Error in removeConnection:", error);
      return rejectWithValue({ 
        message: error.message || "Unknown error removing connection",
        originalError: error.message
      });
    }
  }
);

export const searchUsers = createAsyncThunk(
  'connections/searchUsers',
  async (queryParam, { rejectWithValue, getState }) => {
    try {
      // Handle both direct string and object with query property
      const query = typeof queryParam === 'string' ? queryParam : queryParam.query;
      const userId = typeof queryParam !== 'string' ? queryParam.userId : undefined;
      
      // Log the search query for debugging
      console.log(`Searching users with query: "${query}"`, userId ? `for userId: ${userId}` : '');
      
      // Get the current search results from state
      const currentResults = getState().connections.searchResults;
      
      // If we already have results for this query, return them
      if (getState().connections.lastSearchQuery === query && currentResults.length > 0) {
        console.log(`Using cached results for query: "${query}"`);
        return { users: currentResults, query };
      }
      
      // Otherwise, make the API call
      const response = await connectionService.searchUsers(query, userId);
      console.log('Search API response:', response);
      
      // Normalize the response to ensure we have a consistent format
      let normalizedUsers = [];
      
      if (Array.isArray(response)) {
        // Direct array of users
        normalizedUsers = response;
      } else if (response && response.users && Array.isArray(response.users)) {
        // Object with users array
        normalizedUsers = response.users;
      } else if (response && typeof response === 'object') {
        // Handle case where the API might return users directly in the response object
        // Convert object to array if necessary (for cases where API returns {userId1: userData1, userId2: userData2})
        if (!Array.isArray(response)) {
          const keys = Object.keys(response);
          if (keys.length > 0 && typeof response[keys[0]] === 'object') {
            normalizedUsers = keys.map(key => ({
              id: key,
              ...response[key]
            }));
          } else {
            // It's a single user object, not an array
            normalizedUsers = [response];
          }
        } else {
          normalizedUsers = response;
        }
      }
      
      console.log('Normalized search results:', normalizedUsers);
      
      // Add the query to the response for tracking
      return { users: normalizedUsers, query };
    } catch (error) {
      console.error('Search error:', error);
      return rejectWithValue({ message: error.message });
    }
  }
);

// Initial state
const initialState = {
  connections: {
    family: [],
    friends: [],
    business: []
  },
  pendingConnections: {
    incoming: [],
    outgoing: []
  },
  searchResults: [],
  lastSearchQuery: '',
  loading: {
    connections: false,
    pendingConnections: false,
    sendRequest: false,
    acceptRequest: false,
    rejectRequest: false,
    removeConnection: false,
    searchUsers: false
  },
  error: {
    connections: null,
    pendingConnections: null,
    sendRequest: null,
    acceptRequest: null,
    rejectRequest: null,
    removeConnection: null,
    searchUsers: null
  },
  currentUserId: null
};

// Helper function to ensure state structure is properly initialized
const ensureStateStructure = (state) => {
  // Initialize top-level objects if they don't exist
  if (!state.connections) {
    state.connections = { family: [], friends: [], business: [] };
  }
  
  // Ensure all connection arrays exist
  if (!state.connections.family) state.connections.family = [];
  if (!state.connections.friends) state.connections.friends = [];
  if (!state.connections.business) state.connections.business = [];
  
  // Initialize pendingConnections
  if (!state.pendingConnections) {
    state.pendingConnections = { incoming: [], outgoing: [] };
  }
  
  // Ensure both incoming and outgoing arrays exist
  if (!state.pendingConnections.incoming) state.pendingConnections.incoming = [];
  if (!state.pendingConnections.outgoing) state.pendingConnections.outgoing = [];
  
  // Initialize loading and error objects if they don't exist
  if (!state.loading) {
    state.loading = {
      connections: false,
      pendingConnections: false,
      sendRequest: false,
      acceptRequest: false,
      rejectRequest: false,
      removeConnection: false,
      searchUsers: false
    };
  }
  
  if (!state.error) {
    state.error = {
      connections: null,
      pendingConnections: null,
      sendRequest: null,
      acceptRequest: null,
      rejectRequest: null,
      removeConnection: null,
      searchUsers: null
    };
  }
  
  // Initialize other properties
  if (!state.searchResults) state.searchResults = [];
  if (!state.lastSearchQuery) state.lastSearchQuery = '';
};

// Slice
const connectionSlice = createSlice({
  name: 'connections',
  initialState,
  reducers: {
    resetStatus: (state, action) => {
      ensureStateStructure(state);
      const statusKey = action.payload;
      if (statusKey && state.loading[statusKey]) {
        state.loading[statusKey] = false;
        state.error[statusKey] = null;
      }
    },
    setCurrentUserId: (state, action) => {
      ensureStateStructure(state);
      state.currentUserId = action.payload;
      console.log('connectionSlice: Set current user ID to', action.payload);
    },
    addRealTimeRequest: (state, action) => {
      ensureStateStructure(state);
      const request = action.payload;
      console.log('connectionSlice: Adding real-time request:', request);
      
      if (!request) {
        console.warn('connectionSlice: Invalid request object received');
        return;
      }
      
      // Prepare request in a standard format
      const standardizedRequest = {
        id: request.id || request._id,
        requesterId: request.requesterId,
        recipientId: request.recipientId,
        requestType: request.requestType || request.type,
        message: request.message || '',
        status: request.status || 'pending',
        createdAt: request.createdAt || new Date().toISOString(),
        updatedAt: request.updatedAt || new Date().toISOString(),
        viewed: request.viewed || false,
        requester: request.requester || null
      };
      
      console.log('connectionSlice: Standardized real-time request:', standardizedRequest);
      
      // If we're the recipient, add to incoming requests
      if (standardizedRequest.recipientId === state.currentUserId) {
        console.log('connectionSlice: Adding to incoming requests');
        
        // Check for duplicates first
        const isDuplicate = state.pendingConnections.incoming.some(
          req => req.id === standardizedRequest.id || 
                (req.requesterId === standardizedRequest.requesterId && 
                 req.recipientId === standardizedRequest.recipientId)
        );
        
        if (!isDuplicate) {
          state.pendingConnections.incoming.push(standardizedRequest);
          console.log('connectionSlice: Added to incoming requests');
        } else {
          console.log('connectionSlice: Duplicate request, not adding');
        }
      }
      // If we're the requester, add to outgoing requests
      else if (standardizedRequest.requesterId === state.currentUserId) {
        console.log('connectionSlice: Adding to outgoing requests');
        
        // Check for duplicates
        const isDuplicate = state.pendingConnections.outgoing.some(
          req => req.id === standardizedRequest.id || 
                (req.requesterId === standardizedRequest.requesterId && 
                 req.recipientId === standardizedRequest.recipientId)
        );
        
        if (!isDuplicate) {
          state.pendingConnections.outgoing.push(standardizedRequest);
          console.log('connectionSlice: Added to outgoing requests');
        } else {
          console.log('connectionSlice: Duplicate request, not adding');
        }
      } else {
        console.log('connectionSlice: Request not related to current user, ignoring');
      }
    }
  },
  extraReducers: (builder) => {
    // fetchUserConnections
    builder.addCase(fetchUserConnections.pending, (state) => {
      ensureStateStructure(state);
      state.loading.connections = true;
      state.error.connections = null;
    });
    builder.addCase(fetchUserConnections.fulfilled, (state, action) => {
      ensureStateStructure(state);
      state.loading.connections = false;
      
      // Log the API response
      console.log('API response for connections:', action.payload);
      
      // Organize connections by type
      if (action.payload && action.payload.connections) {
        const connections = action.payload.connections;
        
        // Reset connections
        state.connections = {
          family: [],
          friends: [],
          business: []
        };
        
        // Add each connection to the appropriate category
        connections.forEach(connection => {
          // Log each connection for debugging
          console.log('Processing connection:', connection);
          
          // Get the connection type and normalize it
          let type = (connection.connectionType || connection.type || 'friends').toLowerCase();
          
          // Special handling for type mapping
          if (type === 'family' || type === 'families') {
            state.connections.family.push(connection);
          } else if (type === 'friend' || type === 'friends') {
            state.connections.friends.push(connection);
          } else {
            // If type is not one of our predefined categories (family/friends), add to business/others
            state.connections.business.push(connection);
          }
        });
        
        console.log('Processed connections stored in Redux:', state.connections);
      } else if (action.payload && Array.isArray(action.payload)) {
        // Handle case where API directly returns array of connections without wrapper
        const connections = action.payload;
        
        // Reset connections
        state.connections = {
          family: [],
          friends: [],
          business: []
        };
        
        // Add each connection to the appropriate category
        connections.forEach(connection => {
          // Log each connection for debugging
          console.log('Processing connection (array format):', connection);
          
          // Get the connection type and normalize it
          let type = (connection.connectionType || connection.type || 'friends').toLowerCase();
          
          // Special handling for type mapping
          if (type === 'family' || type === 'families') {
            state.connections.family.push(connection);
          } else if (type === 'friend' || type === 'friends') {
            state.connections.friends.push(connection);
          } else {
            // If type is not one of our predefined categories (family/friends), add to business/others
            state.connections.business.push(connection);
          }
        });
        
        console.log('Processed connections from array format:', state.connections);
      } else {
        console.warn('Unexpected API response format for connections:', action.payload);
      }
    });
    builder.addCase(fetchUserConnections.rejected, (state, action) => {
      ensureStateStructure(state);
      state.loading.connections = false;
      state.error.connections = action.payload?.message || 'Failed to fetch connections';
    });
    
    // fetchPendingConnections
    builder.addCase(fetchPendingConnections.pending, (state) => {
      ensureStateStructure(state);
      state.loading.pendingConnections = true;
      state.error.pendingConnections = null;
    });
    builder.addCase(fetchPendingConnections.fulfilled, (state, action) => {
      ensureStateStructure(state);
      state.loading.pendingConnections = false;
      
      const direction = action.payload.direction || 'incoming';
      const requests = action.payload.requests || [];
      
      console.log(`Received ${requests.length} ${direction} pending connection requests:`, requests);
      
      // Update the appropriate direction
      state.pendingConnections[direction] = requests.map(request => {
        // Ensure all necessary IDs are present
        const normalizedRequest = {
          ...request,
          // Ensure requestId is always set
          id: request.id || request._id || request.requestId || `req-${Date.now()}`,
          requestId: request.requestId || request._id || request.id || `req-${Date.now()}`,
          _id: request._id || request.id || request.requestId || `req-${Date.now()}`
        };
        
        // Log each request for debugging
        console.log(`Adding ${direction} request:`, {
          id: normalizedRequest.id,
          type: normalizedRequest.requestType || normalizedRequest.type,
          requesterName: normalizedRequest.requester?.name,
          recipientName: normalizedRequest.recipient?.name
        });
        
        return normalizedRequest;
      });
      
      // Log the final state after update
      console.log(`Updated ${direction} pending connections state:`, state.pendingConnections[direction]);
    });
    builder.addCase(fetchPendingConnections.rejected, (state, action) => {
      ensureStateStructure(state);
      state.loading.pendingConnections = false;
      state.error.pendingConnections = action.payload?.message || 'Failed to fetch pending connections';
    });
    
    // sendConnectionRequest
    builder.addCase(sendConnectionRequest.pending, (state) => {
      ensureStateStructure(state);
      state.loading.sendRequest = true;
      state.error.sendRequest = null;
    });
    builder.addCase(sendConnectionRequest.fulfilled, (state, action) => {
      ensureStateStructure(state);
      state.loading.sendRequest = false;
      state.status = 'idle';
      state.error = null;
      
      const { payload } = action;
      console.log('connectionSlice: Connection request sent successfully:', payload);
      
      // Format the new request with more robust fallbacks
      const newRequest = {
        id: payload.requestId || payload._id || payload.id || `req-${Date.now()}`,
        requesterId: payload.requesterId || state.currentUserId,
        recipientId: payload.recipientId,
        requestType: payload.requestType || payload.type || 'friend',
        message: payload.message || '',
        status: payload.status || 'pending',
        createdAt: payload.createdAt || new Date().toISOString(),
        updatedAt: payload.updatedAt || new Date().toISOString()
      };
      
      // Only add if we have both requester and recipient IDs
      if (newRequest.requesterId && newRequest.recipientId) {
        // Check if this request already exists to avoid duplicates
        const isDuplicate = state.pendingConnections.outgoing.some(
          req => (req.id === newRequest.id) || 
                (req.requesterId === newRequest.requesterId && 
                 req.recipientId === newRequest.recipientId)
        );
        
        if (!isDuplicate) {
          // Add to outgoing requests
          state.pendingConnections.outgoing.push(newRequest);
        }
      } else {
        console.warn('Cannot add connection request: Missing requester or recipient ID', newRequest);
      }
    });
    builder.addCase(sendConnectionRequest.rejected, (state, action) => {
      ensureStateStructure(state);
      state.loading.sendRequest = false;
      state.error.sendRequest = action.payload?.message || 'Failed to send connection request';
    });
    
    // sendBatchConnectionRequests
    builder.addCase(sendBatchConnectionRequests.pending, (state) => {
      ensureStateStructure(state);
      state.loading.sendRequest = true;
      state.error.sendRequest = null;
    });
    builder.addCase(sendBatchConnectionRequests.fulfilled, (state, action) => {
      ensureStateStructure(state);
      state.loading.sendRequest = false;
      
      const { responses, recipientIds, requestType } = action.payload;
      console.log('Batch connection requests sent successfully:', responses);
      
      // Add each request to the outgoing requests array
      if (Array.isArray(responses)) {
        responses.forEach((response, index) => {
          // Format the new request - handle different response formats
          const requesterId = response.requesterId || state.currentUserId;
          const recipientId = response.recipientId || 
                             (recipientIds && index < recipientIds.length ? recipientIds[index] : null);
          
          if (!recipientId) {
            console.warn('Could not determine recipientId for request:', response);
            return; // Skip this entry
          }
          
          const newRequest = {
            id: response.requestId || response._id || response.id || `req-${Date.now()}-${index}`,
            requesterId: requesterId,
            recipientId: recipientId,
            requestType: response.requestType || requestType || 'friend',
            message: response.message || '',
            status: response.status || 'pending',
            createdAt: response.createdAt || new Date().toISOString(),
            updatedAt: response.updatedAt || new Date().toISOString()
          };
          
          // Add to outgoing requests if not already present
          const isDuplicate = state.pendingConnections.outgoing.some(
            req => (req.id === newRequest.id) || 
                  (req.requesterId === newRequest.requesterId && 
                   req.recipientId === newRequest.recipientId)
          );
          
          if (!isDuplicate) {
            state.pendingConnections.outgoing.push(newRequest);
          }
        });
      }
    });
    builder.addCase(sendBatchConnectionRequests.rejected, (state, action) => {
      ensureStateStructure(state);
      state.loading.sendRequest = false;
      state.error.sendRequest = action.payload?.message || 'Failed to send batch connection requests';
    });
    
    // acceptConnection
    builder.addCase(acceptConnection.pending, (state) => {
      ensureStateStructure(state);
      state.loading.acceptRequest = true;
      state.error.acceptRequest = null;
    });
    builder.addCase(acceptConnection.fulfilled, (state, action) => {
      ensureStateStructure(state);
      state.loading.acceptRequest = false;
      
      // Remove from pending requests
      const requestId = action.payload.requestId;
      state.pendingConnections.incoming = state.pendingConnections.incoming.filter(
        req => req.requestId !== requestId && req.id !== requestId && req._id !== requestId
      );
      
      // Add to connections if connection data is provided
      if (action.payload.connection) {
        const connection = action.payload.connection;
        const type = connection.type?.toLowerCase() || 'friends';
        
        if (state.connections[type]) {
          state.connections[type].push(connection);
        } else {
          // Default to friends
          state.connections.friends.push(connection);
        }
      }
    });
    builder.addCase(acceptConnection.rejected, (state, action) => {
      ensureStateStructure(state);
      state.loading.acceptRequest = false;
      state.error.acceptRequest = action.payload?.message || 'Failed to accept connection request';
    });
    
    // rejectConnection
    builder.addCase(rejectConnection.pending, (state) => {
      ensureStateStructure(state);
      state.loading.rejectRequest = true;
      state.error.rejectRequest = null;
    });
    builder.addCase(rejectConnection.fulfilled, (state, action) => {
      ensureStateStructure(state);
      state.loading.rejectRequest = false;
      
      // Remove from pending requests
      const requestId = action.payload.requestId;
      state.pendingConnections.incoming = state.pendingConnections.incoming.filter(
        req => req.requestId !== requestId && req.id !== requestId && req._id !== requestId
      );
    });
    builder.addCase(rejectConnection.rejected, (state, action) => {
      ensureStateStructure(state);
      state.loading.rejectRequest = false;
      state.error.rejectRequest = action.payload?.message || 'Failed to reject connection request';
    });
    
    // removeConnection
    builder.addCase(removeConnection.pending, (state) => {
      ensureStateStructure(state);
      state.loading.removeConnection = true;
      state.error.removeConnection = null;
    });
    builder.addCase(removeConnection.fulfilled, (state, action) => {
      ensureStateStructure(state);
      state.loading.removeConnection = false;
      
      // Extract connection ID to filter by
      const connectionId = action.payload.connectionId;
      
      // Remove connection from all connection types
      Object.keys(state.connections).forEach(type => {
        state.connections[type] = state.connections[type].filter(connection => {
          // Check all possible ID fields
          const connectionObjectId = connection.id || connection._id || 
                                    connection.connectionId || connection.connectedUserId;
          
          return connectionObjectId !== connectionId;
        });
      });
    });
    builder.addCase(removeConnection.rejected, (state, action) => {
      ensureStateStructure(state);
      state.loading.removeConnection = false;
      state.error.removeConnection = action.payload?.message || 'Failed to remove connection';
    });
    
    // searchUsers
    builder.addCase(searchUsers.pending, (state) => {
      ensureStateStructure(state);
      state.loading.searchUsers = true;
      state.error.searchUsers = null;
    });
    builder.addCase(searchUsers.fulfilled, (state, action) => {
      ensureStateStructure(state);
      state.loading.searchUsers = false;
      console.log('ConnectionSlice: Processing search results payload:', action.payload);
      
      // Check if the response is already in the expected format {users: [...]}
      if (action.payload && action.payload.users) {
        console.log('ConnectionSlice: Found users array in payload:', action.payload.users.length, 'users');
        state.searchResults = action.payload.users.filter(user => user && (user.id || user._id));
        state.lastSearchQuery = action.payload.query || '';
        return;
      }
      
      // Handle various other possible formats for search results
      let searchResultsArray = [];
      
      if (Array.isArray(action.payload)) {
        // Direct array of users
        searchResultsArray = action.payload;
      } else if (typeof action.payload === 'object') {
        // Might be a single user or custom format
        if (action.payload.id || action.payload.name) {
          // Single user object
          searchResultsArray = [action.payload]; 
        }
      }
      
      // Ensure all items in the array have an id property
      searchResultsArray = searchResultsArray.filter(user => user && (user.id || user._id));
      
      console.log('ConnectionSlice: Processed search results:', searchResultsArray.length, 'users');
      
      state.searchResults = searchResultsArray;
      state.lastSearchQuery = action.payload.query || '';
    });
    builder.addCase(searchUsers.rejected, (state, action) => {
      ensureStateStructure(state);
      state.loading.searchUsers = false;
      state.error.searchUsers = action.payload?.message || 'Failed to search users';
      console.error('ConnectionSlice: Search users rejected:', action.payload);
      // Clear search results on error
      state.searchResults = [];
    });
  }
});

export const { resetStatus, setCurrentUserId, addRealTimeRequest } = connectionSlice.actions;
export default connectionSlice.reducer; 